import type { IceServerConfig, TransportMetrics } from '../../runtime/types';

export type TrackHandler = (stream: MediaStream) => void;
export type ConnectionFailedHandler = (state: RTCPeerConnectionState) => void;

/**
 * One InteractiveTransportSession per SDK session.
 * Owns a single RTCPeerConnection for the session lifetime.
 *
 * Phase 1: P2P WebRTC with H264.
 * Camera is the offerer; SDK is the answerer.
 * ICE is gathered to completion before sending the answer (non-trickle),
 * matching signaling-server-python + webrtc-webclient behaviour.
 */
export class InteractiveTransportSession {
  private pc: RTCPeerConnection | null = null;
  private trackHandler: TrackHandler | null = null;
  private failedHandler: ConnectionFailedHandler | null = null;
  private pendingStream: MediaStream | null = null;

  init(iceServers: IceServerConfig[]): void {
    if (this.pc) this.close();

    const config: RTCConfiguration = {
      bundlePolicy: 'max-bundle',
      iceServers: iceServers.map((s) => ({
        urls: s.urls,
        username: s.username,
        credential: s.credential,
      })),
    };

    this.pc = new RTCPeerConnection(config);
    this.pc.ontrack = (event: RTCTrackEvent) => {
      const stream = event.streams[0] ?? new MediaStream([event.track]);
      this.pendingStream = stream;
      this.trackHandler?.(stream);
    };
    this.pc.addEventListener('connectionstatechange', () => {
      const state = this.pc?.connectionState;
      if (state === 'failed' || state === 'disconnected') {
        this.failedHandler?.(state as RTCPeerConnectionState);
      }
    });
  }

  /** Register track handler. If track already arrived, calls handler immediately. */
  onTrack(handler: TrackHandler): void {
    this.trackHandler = handler;
    if (this.pendingStream) handler(this.pendingStream);
  }

  /**
   * Register a handler called when the peer connection enters 'failed' or
   * 'disconnected' state so the session layer can schedule a reconnect.
   */
  onConnectionFailed(handler: ConnectionFailedHandler): void {
    this.failedHandler = handler;
  }

  /**
   * Process an SDP offer from the camera.
   * Returns the full SDP answer (ICE candidates included) to relay back.
   */
  async handleOffer(remoteSdp: string): Promise<string> {
    if (!this.pc) throw new Error('InteractiveTransport not initialised.');
    await this.pc.setRemoteDescription({ type: 'offer', sdp: remoteSdp });
    const answer = await this.pc.createAnswer();
    await this.pc.setLocalDescription(answer);
    await this.waitGatheringComplete();
    return this.pc.localDescription?.sdp ?? '';
  }

  async getMetrics(): Promise<TransportMetrics> {
    if (!this.pc) return {};
    const report = await this.pc.getStats();

    // RTT: candidate-pair.currentRoundTripTime (ICE STUN ping).
    // VGPS is receive-only P2P — remote-inbound-rtp is often absent.
    // remote-inbound-rtp.roundTripTime only as fallback.
    // See docs/qos/design-qos.md §Transport Metrics Interface.
    let rttMs: number | undefined;
    // Jitter: inbound-rtp.jitter (browser measures from incoming stream).
    // remote-inbound-rtp.jitter is camera's jitter measurement — wrong direction.
    let jitterMs: number | undefined;
    let jitterBufferDelayMs: number | undefined;
    let framesPerSecond: number | undefined;
    let availableIncomingBitrateKbps: number | undefined;
    let lostPackets = 0;
    let totalPackets = 0;
    let bytesReceived = 0;
    let frameWidth: number | undefined;
    let frameHeight: number | undefined;
    let framesDropped: number | undefined;
    let decoderImplementation: string | undefined;
    let powerEfficientDecoder: boolean | undefined;

    report.forEach((stat) => {
      const s = stat as RTCStats & Record<string, unknown>;

      if (s.type === 'candidate-pair' && s['nominated'] === true) {
        if (typeof s['currentRoundTripTime'] === 'number') {
          rttMs = (s['currentRoundTripTime'] as number) * 1000;
        }
        if (typeof s['availableIncomingBitrate'] === 'number') {
          availableIncomingBitrateKbps = (s['availableIncomingBitrate'] as number) / 1000;
        }
      }

      if (s.type === 'inbound-rtp') {
        // Correct jitter source: receiver-side measurement
        if (typeof s['jitter'] === 'number') jitterMs = (s['jitter'] as number) * 1000;
        if (typeof s['packetsLost'] === 'number') lostPackets += s['packetsLost'] as number;
        if (typeof s['packetsReceived'] === 'number') totalPackets += s['packetsReceived'] as number;
        if (typeof s['bytesReceived'] === 'number') bytesReceived += s['bytesReceived'] as number;

        if (typeof s['framesPerSecond'] === 'number') framesPerSecond = s['framesPerSecond'] as number;
        if (typeof s['frameWidth'] === 'number') frameWidth = s['frameWidth'] as number;
        if (typeof s['frameHeight'] === 'number') frameHeight = s['frameHeight'] as number;
        if (typeof s['framesDropped'] === 'number') framesDropped = s['framesDropped'] as number;
        if (typeof s['decoderImplementation'] === 'string') decoderImplementation = s['decoderImplementation'] as string;
        if (typeof s['powerEfficientDecoder'] === 'boolean') powerEfficientDecoder = s['powerEfficientDecoder'] as boolean;
        const delay = s['jitterBufferDelay'] as number | undefined;
        const emitted = s['jitterBufferEmittedCount'] as number | undefined;
        if (delay !== undefined && emitted && emitted > 0) {
          jitterBufferDelayMs = (delay / emitted) * 1000;
        }
      }

      // RTT fallback from remote-inbound-rtp if candidate-pair not yet available
      if (s.type === 'remote-inbound-rtp' && rttMs === undefined) {
        if (typeof s['roundTripTime'] === 'number') {
          rttMs = (s['roundTripTime'] as number) * 1000;
        }
      }
    });

    return {
      rttMs,
      jitterMs,
      jitterBufferDelayMs,
      framesPerSecond,
      availableIncomingBitrateKbps,
      packetLossRatio: totalPackets > 0 ? lostPackets / (totalPackets + lostPackets) : undefined,
      bytesReceived: bytesReceived > 0 ? bytesReceived : undefined,
      frameWidth,
      frameHeight,
      framesDropped,
      decoderImplementation,
      powerEfficientDecoder,
    };
  }

  close(): void {
    this.pc?.close();
    this.pc = null;
    this.pendingStream = null;
  }

  // ── Private ──────────────────────────────────────────────────────────────

  private waitGatheringComplete(): Promise<void> {
    return new Promise((resolve) => {
      if (!this.pc || this.pc.iceGatheringState === 'complete') {
        resolve();
        return;
      }
      const onchange = () => {
        if (this.pc?.iceGatheringState === 'complete') {
          this.pc.removeEventListener('icegatheringstatechange', onchange);
          resolve();
        }
      };
      this.pc.addEventListener('icegatheringstatechange', onchange);
    });
  }
}
